/**
 * Demo mock state for the Gable "Autopilot" preview UI (SD-7384 north star).
 *
 * SINGLE SOURCE OF TRUTH for every count and derived copy shared between the
 * drawer header status line, the Autopilot/Activity tab badges, the hero
 * status strip, the Autopilot settings panel and the Activity panel — all
 * surfaces MUST read from here so they stay in sync (design-spec hard rule).
 *
 * Pure data — no Angular imports. Text fields may contain `**bold**` segments
 * that consuming templates render as <strong> (see renderBold helpers in the
 * consuming components).
 */

export type GableAutopilotMode = 'off' | 'suggest' | 'auto';

/** Domain tint for 38-44px icon tiles: requests=cyan, violations=orange, payments=green. */
export type GableIconTone = 'cyan' | 'orange' | 'green';

export interface GableAutopilotRule {
    /** Row title; for limit rules this is the prefix ("Approve maintenance under"). */
    title: string;
    description: string;
    enabled: boolean;
    /** Locked = guardrail: no switch, "Always asks you" label, never automatable. */
    locked?: boolean;
    /** Present on limit rules: currently-selected option of the inline mini-select. */
    limitLabel?: string;
    /** Present on limit rules: all mini-select options (selected = limitLabel). */
    limitOptions?: string[];
}

export interface GableAutopilotArea {
    key: 'requests' | 'violations' | 'payments';
    title: string;
    description: string;
    /** Phosphor glyph class, rendered as <i class="ph-bold {iconClass}">. */
    iconClass: string;
    iconTone: GableIconTone;
    mode: GableAutopilotMode;
    rules: GableAutopilotRule[];
    /** Derived: enabled non-locked rule titles (with limit appended) — "RUNS WITHOUT ASKING" list. */
    runsWithoutAsking: string[];
    /** Derived: locked rule titles — "ALWAYS ASKS YOU FIRST" list. */
    alwaysAsks: string[];
}

export interface GableEvidenceLine {
    /** Phosphor glyph class (ph-calendar-blank | ph-camera | ph-check). */
    iconClass: string;
    /** May contain `**bold**` segments. */
    text: string;
}

export interface GableEvidenceSection {
    /** Uppercase section label, e.g. "Notice history". */
    label: string;
    lines: GableEvidenceLine[];
    /** Drafted-text block shown under the lines (may contain `**bold**`). */
    miniEmail?: string;
}

export interface GableReviewItem {
    id: string;
    /** Domain key for filter matching. */
    domain: 'requests' | 'violations' | 'payments';
    /** Phosphor glyph class for the 38px domain tile. */
    iconClass: string;
    iconTone: GableIconTone;
    title: string;
    /** Reasoning/confidence sub-line — every AI recommendation shows its "why" inline. */
    sub: string;
    /** Primary (check-icon) action button label. */
    primaryLabel: string;
    /** Secondary action button label. */
    secondaryLabel: string;
    /** Optional expandable evidence panel ("View evidence" toggle). */
    evidence?: GableEvidenceSection;
}

export interface GableFeedItem {
    id: string;
    domain: 'requests' | 'violations' | 'payments';
    iconClass: string;
    iconTone: GableIconTone;
    title: string;
    meta: string;
    /** auto = "Auto-completed" (green pill), you = "You decided" (gray), review = "Sent to review" (blue). */
    attribution: 'auto' | 'you' | 'review';
    undoable: boolean;
}

export interface GableFeedDay {
    /** Group label, e.g. "Today", "Yesterday". */
    label: string;
    items: GableFeedItem[];
}

export interface GableAutopilotStats {
    /** Agreement-with-you percentage over last 30 days (trust metric). */
    trustPct: number;
    actionsThisWeek: number;
    /** = reviewItems.length; feeds tab badge, header sub-line, launcher, "Waiting on you" tile. */
    needsReview: number;
    /** e.g. "6.2 hrs" — conservative methodology. */
    timeSavedLabel: string;
    /** Items handled in this morning's run — hero status strip. */
    handledThisMorning: number;
    /** Denominators for the trust sentence: "142 of 151 items". */
    trustMatched: number;
    trustTotal: number;
}

export interface GableGuardrail {
    title: string;
    description: string;
}

export interface GableAutopilotMock {
    stats: GableAutopilotStats;
    areas: GableAutopilotArea[];
    guardrails: GableGuardrail[];
    reviewItems: GableReviewItem[];
    feed: GableFeedDay[];
    /** Fields to build "Autopilot on for {areasOnAuto} of 3 areas · {needsReview} items need you". */
    headerStatus: {
        paused: boolean;
        /** Count of areas in 'auto' mode (derived from areas). */
        areasOnAuto: number;
        totalAreas: number;
        /** Prebuilt full string for surfaces that just bind text. */
        text: string;
    };
}

export interface GableHeroCapabilityCard {
    iconClass: string;
    iconTone: GableIconTone | 'purple';
    title: string;
    description: string;
    /** REAL chat prompt sent to the live specialist when the card is clicked. */
    prompt: string;
    /** Override: open the Activity tab instead of sending a chat message —
     *  the live box doesn't know Autopilot exists yet; don't ask it. */
    action?: 'open-activity';
}

export interface GableHero {
    tagline: string;
    statusStrip: {
        title: string;
        description: string;
        ctaLabel: string;
    };
    capabilityCards: GableHeroCapabilityCard[];
}

// ---------------------------------------------------------------------------
// Mock state
// ---------------------------------------------------------------------------

const NEEDS_REVIEW = 5;
const AREAS_ON_AUTO = 2;
const HANDLED_THIS_MORNING = 4;

export const GABLE_AUTOPILOT_MOCK: GableAutopilotMock = {
    stats: {
        trustPct: 94,
        actionsThisWeek: 47,
        needsReview: NEEDS_REVIEW,
        timeSavedLabel: '6.2 hrs',
        handledThisMorning: HANDLED_THIS_MORNING,
        trustMatched: 142,
        trustTotal: 151,
    },

    areas: [
        {
            key: 'requests',
            title: 'Requests',
            description: 'Maintenance, ARC and general owner requests',
            iconClass: 'ph-wrench',
            iconTone: 'cyan',
            mode: 'auto',
            rules: [
                {
                    title: 'Approve maintenance under',
                    description: 'Routine repairs within budget and existing vendor contracts',
                    enabled: true,
                    limitLabel: '$500',
                    limitOptions: ['$250', '$500', '$1,000'],
                },
                {
                    title: 'Assign preferred vendors',
                    description: 'Route approved work to your vendor list by trade, and schedule it',
                    enabled: true,
                },
                {
                    title: 'Acknowledge new requests instantly',
                    description: 'Send the owner a confirmation with an expected timeline',
                    enabled: true,
                },
                {
                    title: 'Approve ARC items matching guidelines',
                    description: 'Colors, fixtures and plans already in the approved palette',
                    enabled: false,
                },
                {
                    title: 'ARC items requiring a board vote',
                    description: 'Structural changes always go to the board — never auto-decided',
                    enabled: false,
                    locked: true,
                },
            ],
            runsWithoutAsking: [
                'Approve maintenance under $500',
                'Assign preferred vendors',
                'Acknowledge new requests instantly',
            ],
            alwaysAsks: ['ARC items requiring a board vote'],
        },
        {
            key: 'violations',
            title: 'Violations',
            description: 'Notices, escalations, reminders and closures',
            iconClass: 'ph-scales',
            iconTone: 'orange',
            mode: 'auto',
            rules: [
                {
                    title: 'Send courtesy & warning notices',
                    description: 'First and second notices use your templates, personalized per owner',
                    enabled: true,
                },
                {
                    title: 'Escalate on your policy timeline',
                    description: 'Move stages when the cure period passes without resolution',
                    enabled: true,
                },
                {
                    title: 'Close on photo evidence',
                    description: 'Owner-submitted photos are verified before a violation is closed',
                    enabled: true,
                },
                {
                    title: 'Issuing fines',
                    description: 'Fines are drafted and queued, but always wait for your approval',
                    enabled: false,
                    locked: true,
                },
            ],
            runsWithoutAsking: [
                'Send courtesy & warning notices',
                'Escalate on your policy timeline',
                'Close on photo evidence',
            ],
            alwaysAsks: ['Issuing fines'],
        },
        {
            key: 'payments',
            title: 'Payments & Transactions',
            description: 'Reconciliation, categorization and payables',
            iconClass: 'ph-bank',
            iconTone: 'green',
            mode: 'suggest',
            rules: [
                {
                    title: 'Reconcile deposits (95%+ match)',
                    description: 'Runs every morning at 7:30 AM — lower-confidence matches come to you',
                    enabled: true,
                },
                {
                    title: 'Categorize to budget categories',
                    description: 'Based on vendor, memo and how you categorized in the past',
                    enabled: true,
                },
                {
                    title: 'Approve recurring payables under',
                    description: 'Known vendors with consistent amounts, under your limit',
                    enabled: true,
                    limitLabel: '$1,000',
                    limitOptions: ['$500', '$1,000', '$2,500'],
                },
                {
                    title: 'Flag duplicates & unusual amounts',
                    description: 'Anything anomalous pauses and comes to you with an explanation',
                    enabled: true,
                },
                {
                    title: 'Payments over your approval limit',
                    description: 'Money leaving accounts above the limit always needs you',
                    enabled: false,
                    locked: true,
                },
            ],
            runsWithoutAsking: [
                'Reconcile deposits (95%+ match)',
                'Categorize to budget categories',
                'Approve recurring payables under $1,000',
                'Flag duplicates & unusual amounts',
            ],
            alwaysAsks: ['Payments over your approval limit'],
        },
    ],

    guardrails: [
        {
            title: 'Issuing fines or charges to owners',
            description: 'Fines are drafted and queued with evidence, but never sent without you',
        },
        {
            title: 'Payments over your approval limit ($1,000)',
            description: 'Money leaving accounts above the limit always needs you',
        },
        {
            title: 'Legal escalations & attorney referrals',
            description: 'Legal steps are never automated in any mode',
        },
    ],

    reviewItems: [
        {
            id: 'rev-1',
            domain: 'violations',
            iconClass: 'ph-scales',
            iconTone: 'orange',
            title: 'Issue $100 fine — Unit 23, trash cans left out (3rd notice)',
            sub: 'Escalation policy reached the fine stage. Notice history attached.',
            primaryLabel: 'Approve fine',
            secondaryLabel: 'Skip this time',
            evidence: {
                label: 'Notice history',
                lines: [
                    { iconClass: 'ph-calendar-blank', text: '**Courtesy notice** — sent May 14, opened May 14' },
                    { iconClass: 'ph-calendar-blank', text: '**1st warning** — sent May 28, opened May 29' },
                    { iconClass: 'ph-calendar-blank', text: '**2nd warning** — sent June 4, no response' },
                    { iconClass: 'ph-camera', text: '**Inspection photo** — June 9, cans still at curb (attached)' },
                ],
                miniEmail: '**Drafted fine notice:** "Per section 9.4 of the community guidelines and after three notices, a $100 fine has been applied to your account…"',
            },
        },
        {
            id: 'rev-2',
            domain: 'violations',
            iconClass: 'ph-scales',
            iconTone: 'orange',
            title: 'Issue $100 fine — Unit 8, unapproved exterior storage',
            sub: 'Two warnings sent; cure period expired June 8.',
            primaryLabel: 'Approve fine',
            secondaryLabel: 'Skip this time',
            evidence: {
                label: 'Notice history',
                lines: [
                    { iconClass: 'ph-calendar-blank', text: '**1st warning** — sent May 22' },
                    { iconClass: 'ph-calendar-blank', text: '**2nd warning** — sent June 1, owner replied "will remove by weekend"' },
                    { iconClass: 'ph-camera', text: '**Inspection photo** — June 9, items still present' },
                ],
            },
        },
        {
            id: 'rev-3',
            domain: 'payments',
            iconClass: 'ph-bank',
            iconTone: 'green',
            title: 'Match $250 payment from "R. Calloway"',
            sub: 'No exact unit match. Closest: Rachel Calloway, Unit 31 (92% confidence).',
            primaryLabel: 'Match to Unit 31',
            secondaryLabel: 'Mark as other income',
            evidence: {
                label: 'Match candidates',
                lines: [
                    { iconClass: 'ph-check', text: '**Unit 31 — Rachel Calloway** · 92% · open balance $250 · memo "June dues"' },
                    { iconClass: 'ph-check', text: '**Unit 13 — R. Callow** · 41% · no open balance' },
                ],
                miniEmail: 'Check #309, deposited June 10 in Operating ····4821. If matched, Unit 31 balance goes to $0.',
            },
        },
        {
            id: 'rev-4',
            domain: 'payments',
            iconClass: 'ph-bank',
            iconTone: 'green',
            title: 'Approve $1,480 payable — Crystal Pools resurfacing deposit',
            sub: 'Over your $1,000 auto-approval limit.',
            primaryLabel: 'Approve payment',
            secondaryLabel: 'Deny',
            evidence: {
                label: 'Budget impact',
                lines: [
                    { iconClass: 'ph-check', text: '**Pool Maintenance budget:** $12,000 — 62% used; this brings it to 74%' },
                    { iconClass: 'ph-check', text: '**Vendor:** Crystal Pools — 4 prior payments, all on contract' },
                    { iconClass: 'ph-check', text: '**Invoice:** INV-2287, due June 20 (attached)' },
                ],
            },
        },
        {
            id: 'rev-5',
            domain: 'requests',
            iconClass: 'ph-wrench',
            iconTone: 'cyan',
            title: 'Send denial — pool key replacement, Unit 118 (3rd this year)',
            sub: 'Outside policy (2 per year). Denial drafted with $25 replacement-fee option.',
            primaryLabel: 'Send denial',
            secondaryLabel: 'Edit reply first',
            evidence: {
                label: 'Drafted reply',
                lines: [],
                miniEmail: '"Hi Alyssa — community policy 7.1 covers two key replacements per year, and this would be the third. You can get a replacement for a $25 fee — just reply here and I will add it to your next statement…"',
            },
        },
    ],

    feed: [
        {
            label: 'Today',
            items: [
                {
                    id: 'feed-1',
                    domain: 'payments',
                    iconClass: 'ph-bank',
                    iconTone: 'green',
                    title: 'Reconciled deposit $9,425.00 — matched 19 of 21 transactions',
                    meta: '7:30 AM · morning run · Operating ····4821',
                    attribution: 'auto',
                    undoable: false,
                },
                {
                    id: 'feed-2',
                    domain: 'payments',
                    iconClass: 'ph-bank',
                    iconTone: 'green',
                    title: 'Categorized 7 transactions to budget categories',
                    meta: '7:31 AM · morning run · avg confidence 97%',
                    attribution: 'auto',
                    undoable: true,
                },
                {
                    id: 'feed-3',
                    domain: 'violations',
                    iconClass: 'ph-scales',
                    iconTone: 'orange',
                    title: 'Sent 14 overdue-violation reminder emails',
                    meta: '7:30 AM · morning run',
                    attribution: 'auto',
                    undoable: false,
                },
                {
                    id: 'feed-4',
                    domain: 'violations',
                    iconClass: 'ph-scales',
                    iconTone: 'orange',
                    title: 'Escalated 6 violations to Stage 2 warning',
                    meta: '7:30 AM · per escalation policy 9.3',
                    attribution: 'auto',
                    undoable: true,
                },
            ],
        },
        {
            label: 'Yesterday',
            items: [
                {
                    id: 'feed-5',
                    domain: 'requests',
                    iconClass: 'ph-wrench',
                    iconTone: 'cyan',
                    title: 'Approved maintenance request — irrigation valve, Unit 112',
                    meta: 'Yesterday 4:18 PM · Brightline Plumbing assigned for Thursday',
                    attribution: 'auto',
                    undoable: true,
                },
                {
                    id: 'feed-6',
                    domain: 'requests',
                    iconClass: 'ph-wrench',
                    iconTone: 'cyan',
                    title: 'Requested missing site plan — ARC fence application, Unit 415',
                    meta: 'Yesterday 4:18 PM',
                    attribution: 'auto',
                    undoable: false,
                },
                {
                    id: 'feed-7',
                    domain: 'requests',
                    iconClass: 'ph-wrench',
                    iconTone: 'cyan',
                    title: 'Acknowledged 3 new requests with expected timelines',
                    meta: 'Yesterday 2:40 PM',
                    attribution: 'auto',
                    undoable: false,
                },
                {
                    id: 'feed-8',
                    domain: 'payments',
                    iconClass: 'ph-bank',
                    iconTone: 'green',
                    title: 'Flagged possible duplicate — check #2241, $450 to GreenScape',
                    meta: 'Yesterday 11:02 AM · paused, not posted',
                    attribution: 'review',
                    undoable: false,
                },
            ],
        },
    ],

    headerStatus: {
        paused: false,
        areasOnAuto: AREAS_ON_AUTO,
        totalAreas: 3,
        text: `Autopilot on for ${AREAS_ON_AUTO} areas · ${NEEDS_REVIEW} items need you`,
    },
};

export const GABLE_HERO: GableHero = {
    tagline: 'I review, decide and act on requests, violations and payments — inside the rules you set. You just track the progress.',

    statusStrip: {
        title: `Autopilot handled ${HANDLED_THIS_MORNING} items this morning`,
        description: 'Reconciled the daily deposit, categorized transactions, sent reminders and escalated per your policy.',
        ctaLabel: `Review ${NEEDS_REVIEW}`,
    },

    capabilityCards: [
        {
            iconClass: 'ph-wrench',
            iconTone: 'cyan',
            title: 'Work down my requests',
            description: 'Triage open requests and get a recommendation on each.',
            // Bounded on purpose: "recommendation for each" sent the specialist
            // reading every request's comment thread (~5 min turns). Cap the
            // deep-read at the few that matter and it's a ~1-min turn.
            prompt: 'Triage my open requests: using statuses and ages only, pick the 5 that most urgently need a decision, then give me a recommendation for just those 5. Do not read through every request.',
        },
        {
            iconClass: 'ph-scales',
            iconTone: 'orange',
            title: 'Handle overdue violations',
            description: 'See what needs a reminder and what should escalate.',
            // Bounded like the requests card: group counts + a few concrete
            // examples per group, not a per-violation deep read.
            prompt: 'Group my overdue violations by how I should act — reminders vs escalations. Give me counts per group plus the 3 most pressing examples in each, with your recommendation. Keep it quick — no need to examine every violation.',
        },
        {
            iconClass: 'ph-bank',
            iconTone: 'green',
            title: 'Reconcile & categorize',
            description: 'Match recent transactions to the right budget categories.',
            prompt: 'Look at recent uncategorized transactions and suggest budget categories',
        },
        {
            iconClass: 'ph-lightning',
            iconTone: 'purple',
            title: "See Autopilot's suggestions",
            description: 'What it worked on recently and what needs your review.',
            prompt: '',
            action: 'open-activity',
        },
    ],
};
